import { AppConfig } from '../api/config';

export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'skymap_access_token',
  ID_TOKEN: 'skymap_id_token',
  REFRESH_TOKEN: 'skymap_refresh_token',
  CODE_VERIFIER: 'skymap_code_verifier',
  STATE: 'skymap_auth_state',
};

function generateRandomString(length: number): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => chars[b % chars.length]).join('');
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  return crypto.subtle.digest('SHA-256', new TextEncoder().encode(plain));
}

function base64urlEncode(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

interface TokenResponse {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
}

function storeTokens(tokens: TokenResponse): void {
  sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
  if (tokens.id_token) {
    sessionStorage.setItem(STORAGE_KEYS.ID_TOKEN, tokens.id_token);
  }
  if (tokens.refresh_token) {
    sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
  }
}

/**
 * Provider-neutral OIDC Authorization Code + PKCE flow. All endpoints, the
 * client id, and the scope list come from GET /api/v1/config — nothing here
 * knows whether Cognito or identity-service/Zitadel is on the other side.
 */
export async function initiateLogin(config: AppConfig): Promise<void> {
  const verifier = generateRandomString(64);
  const challenge = base64urlEncode(await sha256(verifier));
  const state = generateRandomString(32);
  sessionStorage.setItem(STORAGE_KEYS.CODE_VERIFIER, verifier);
  sessionStorage.setItem(STORAGE_KEYS.STATE, state);

  const redirectUri = `${window.location.origin}/auth/callback`;
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: redirectUri,
    code_challenge_method: 'S256',
    code_challenge: challenge,
    scope: config.scopes,
    state,
    // Standard OIDC: show the IdP's account picker instead of silently reusing
    // whichever SSO session is active — staff switch accounts per app this way.
    // IdPs without account selection ignore the parameter.
    prompt: 'select_account',
  });
  window.location.href = `${config.authorizeUrl}?${params.toString()}`;
}

export async function exchangeCode(
  config: AppConfig,
  code: string,
  state: string | null,
): Promise<void> {
  const expectedState = sessionStorage.getItem(STORAGE_KEYS.STATE);
  sessionStorage.removeItem(STORAGE_KEYS.STATE);
  if (!expectedState || state !== expectedState) {
    throw new Error('State mismatch — possible CSRF, please retry the login');
  }

  const verifier = sessionStorage.getItem(STORAGE_KEYS.CODE_VERIFIER);
  if (!verifier) {
    throw new Error('No code verifier found — possible CSRF or session expired');
  }

  const redirectUri = `${window.location.origin}/auth/callback`;
  const res = await fetch(config.tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      redirect_uri: redirectUri,
      code,
      code_verifier: verifier,
    }),
  });

  if (!res.ok) {
    throw new Error('Token exchange failed');
  }

  storeTokens((await res.json()) as TokenResponse);
  sessionStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);
}

/**
 * Exchanges the stored refresh token for a fresh access token. Returns false
 * when there is nothing to refresh or the IdP rejects the grant (e.g. refresh
 * token expired/revoked) — callers should treat that as a signed-out state.
 * A rotated refresh token (Zitadel rotates, Cognito does not) is stored.
 */
export async function refreshTokens(config: AppConfig): Promise<boolean> {
  const refreshToken = sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  if (!refreshToken) {
    return false;
  }

  const res = await fetch(config.tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.clientId,
      refresh_token: refreshToken,
    }),
  });

  if (!res.ok) {
    return false;
  }

  storeTokens((await res.json()) as TokenResponse);
  return true;
}

/** Expiry of the stored access token in epoch milliseconds, or null. */
export function getAccessTokenExpiry(): number | null {
  const token = sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  if (!token) {
    return null;
  }
  const claims = decodeIdTokenClaims(token);
  return typeof claims?.exp === 'number' ? claims.exp * 1000 : null;
}

export function clearTokens(): void {
  Object.values(STORAGE_KEYS).forEach((k) => sessionStorage.removeItem(k));
}

export function decodeIdTokenClaims(idToken: string): Record<string, unknown> | null {
  try {
    const payload = idToken.split('.')[1];
    if (!payload) {
      return null;
    }
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as Record<
      string,
      unknown
    >;
  } catch {
    return null;
  }
}
